// React hook for analytics tracking
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '../utils/analytics';
import type { AnalyticsEvent } from '../utils/analytics';

/**
 * Hook to track page views automatically
 */
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    analytics.trackPageView(location.pathname + location.search);
  }, [location]);
}

/**
 * Hook to get analytics tracking functions
 */
export function useAnalytics() {
  const trackEvent = (event: AnalyticsEvent) => {
    analytics.trackEvent(event);
  };

  const trackPageView = (path?: string, title?: string) => {
    const currentPath = path || window.location.pathname + window.location.search;
    analytics.trackPageView(currentPath, title);
  };

  const grantConsent = async () => {
    await analytics.grantConsent();
  };

  const revokeConsent = () => {
    analytics.revokeConsent();
  };

  const hasConsent = () => {
    return analytics.hasConsent();
  };

  const isConsentRequired = () => {
    return analytics.isConsentRequired();
  };

  return {
    trackEvent,
    trackPageView,
    grantConsent,
    revokeConsent,
    hasConsent,
    isConsentRequired,
  };
}

/**
 * Predefined event tracking functions
 */
export function useEventTracking() {
  const { trackEvent } = useAnalytics();

  const trackJobApplication = (jobId: string, jobTitle: string) => {
    trackEvent({
      action: 'job_application_submitted',
      category: 'Job Application',
      label: jobTitle,
      value: parseInt(jobId, 10) || undefined,
    });
  };

  const trackResumeUpload = (_resumeId: string, fileName: string) => {
    trackEvent({
      action: 'resume_uploaded',
      category: 'Resume',
      label: fileName,
    });
  };

  const trackUserRegistration = (_userId: string, method: string) => {
    trackEvent({
      action: 'user_registered',
      category: 'User',
      label: method,
    });
  };

  const trackJobView = (jobId: string, jobTitle: string) => {
    trackEvent({
      action: 'job_viewed',
      category: 'Job',
      label: jobTitle,
      value: parseInt(jobId, 10) || undefined,
    });
  };

  const trackJobSearch = (searchTerm: string, resultsCount: number) => {
    trackEvent({
      action: 'job_searched',
      category: 'Job',
      label: searchTerm,
      value: resultsCount,
    });
  };

  const trackProfileUpdate = (section: string) => {
    trackEvent({
      action: 'profile_updated',
      category: 'Profile',
      label: section,
    });
  };

  return {
    trackJobApplication,
    trackResumeUpload,
    trackUserRegistration,
    trackJobView,
    trackJobSearch,
    trackProfileUpdate,
  };
}
